// Configuración base para servicios API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: HeadersInit;
  body?: any;
  params?: Record<string, string | number | boolean>;
}

class ApiService {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private getAuthToken(): string {
    return localStorage.getItem('accessToken') || '';
  }

  private getDefaultHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean>): string {
    const url = new URL(endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  async request<T = any>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    try {
      const {
        method = 'GET',
        headers: customHeaders = {},
        body,
        params
      } = config;

      const url = this.buildUrl(endpoint, params);
      const headers = {
        ...this.getDefaultHeaders(),
        ...customHeaders
      };

      const requestOptions: RequestInit = {
        method,
        headers,
      };

      if (body && method !== 'GET') {
        requestOptions.body = JSON.stringify(body);
      }

      const response = await fetch(url, requestOptions);
      
      // Intentar parsear como JSON
      let responseData: any;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      if (!response.ok) {
        // Manejar errores 401 (no autorizado / token expirado)
        if (response.status === 401) {
          console.warn('🔒 Sesión expirada o no autorizada, redirigiendo a login...');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('macartur:user');
          localStorage.removeItem('macartur:lastSelectedLocation');
          // Redirigir al login en lugar de recargar
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }

        // Si la respuesta tiene formato de error estándar
        if (typeof responseData === 'object' && responseData.error) {
          throw new Error(responseData.error);
        }
        
        // Si es texto, usar ese mensaje
        if (typeof responseData === 'string') {
          throw new Error(responseData);
        }

        // Mensaje de error genérico basado en status
        const errorMessage = this.getErrorMessageFromStatus(response.status);
        throw new Error(errorMessage);
      }

      return responseData;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error de conexión');
    }
  }

  private getErrorMessageFromStatus(status: number): string {
    switch (status) {
      case 400:
        return 'Solicitud inválida';
      case 401:
        return 'No autorizado';
      case 403:
        return 'Acceso denegado';
      case 404:
        return 'Recurso no encontrado';
      case 409:
        return 'Conflicto - El recurso ya existe';
      case 422:
        return 'Datos inválidos';
      case 500:
        return 'Error interno del servidor';
      case 503:
        return 'Servicio no disponible';
      default:
        return `Error del servidor (${status})`;
    }
  }

  // Métodos de conveniencia
  async get<T = any>(endpoint: string, params?: Record<string, string | number | boolean>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  async post<T = any>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body });
  }

  async put<T = any>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body });
  }

  async patch<T = any>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body });
  }

  async delete<T = any>(endpoint: string, config?: { data?: any }): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE', body: config?.data });
  }

  // Método para subir archivos
  async uploadFile<T = any>(
    endpoint: string, 
    file: File, 
    fieldName: string = 'file',
    additionalData?: Record<string, any>
  ): Promise<ApiResponse<T>> {
    try {
      const formData = new FormData();
      formData.append(fieldName, file);

      if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
          formData.append(key, String(value));
        });
      }

      const headers: HeadersInit = {};
      const token = this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Error al subir archivo');
      }

      return responseData;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Error al subir archivo');
    }
  }

  // Configurar base URL dinámicamente
  setBaseURL(url: string): void {
    this.baseURL = url;
  }

  // Obtener base URL actual
  getBaseURL(): string {
    return this.baseURL;
  }
}

// Instancia singleton del cliente API
export const apiClient = new ApiService();

// Exportar la clase para casos donde se necesite múltiples instancias
export { ApiService };

// Tipos para uso en otros servicios
export type { ApiResponse, RequestConfig };

/**
 * Función de fetch con manejo automático de autenticación y errores 401
 * Usar como reemplazo directo de fetch() en los servicios
 */
export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('accessToken');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Si es 401, limpiar sesión y redirigir a login
  if (response.status === 401) {
    console.warn('🔒 Sesión expirada o no autorizada, redirigiendo a login...');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('macartur:user');
    localStorage.removeItem('macartur:lastSelectedLocation');
    window.location.href = '/login';
    throw new Error('Sesión expirada');
  }

  return response;
};

/**
 * Helper para obtener el token de autenticación
 */
export const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('accessToken');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};